import { z } from 'zod';
import { ExecutionCapabilitySchema, capabilityKey } from './run-capabilities';
import { RunStatusSchema } from './runs';

/**
 * Parallel cross-browser fan-out contracts (Migration 064).
 *
 * The launcher submits one parent request listing N capability
 * cells; the API mints a parent run id and one child run id per
 * cell. Children execute concurrently (subject to plan
 * `parallelWorkers`); when each child finishes the recorder POSTs
 * its result with `parentRunId` set, and the API recomputes the
 * parent's status.
 *
 * The parent row carries `is_parent = true` and `status` aggregated
 * from children (see `aggregateChildStatus`). Parent rows never
 * execute steps — they are pure aggregates — and are excluded from
 * step-result analytics.
 */

export const PARENT_RUN_STATUSES = ['queued', 'running', 'passed', 'failed', 'partial', 'cancelled'] as const;
export type ParentRunStatus = (typeof PARENT_RUN_STATUSES)[number];
export const ParentRunStatusSchema = z.enum(PARENT_RUN_STATUSES);

/**
 * `partial` is a parent-only state — exists when at least one child
 * passed and at least one failed. Children themselves never carry
 * this status; they are always one of the leaf RunStatus values.
 */

export const StartParallelRunRequestSchema = z.object({
  flowId: z.string().min(1),
  flowName: z.string().min(1).optional(),
  projectId: z.string().uuid().optional(),
  environment: z.string().min(1).optional(),
  /** Min 2 cells — anything less is a regular (non-parallel) run. */
  capabilities: z.array(ExecutionCapabilitySchema).min(2).max(20),
  /**
   * Optional caller-controlled execution mode (cloud vs local).
   * Resolver applies the same rules as a single run; missing means
   * use org default.
   */
  executionMode: z.enum(['local', 'cloud']).optional(),
  /** QD-1 Prerequisite #1 — optional CI build identity (never manufactured). */
  buildId: z.string().trim().min(1).max(120).nullable().optional(),
  commitSha: z.string().trim().min(7).max(64).nullable().optional(),
});
export type StartParallelRunRequest = z.infer<typeof StartParallelRunRequestSchema>;

export const ParallelChildSchema = z.object({
  runId: z.string().uuid(),
  capability: ExecutionCapabilitySchema,
  capabilityKey: z.string(),
  status: RunStatusSchema,
  durationMs: z.number().int().nullable(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
});
export type ParallelChild = z.infer<typeof ParallelChildSchema>;

export const StartParallelRunResponseSchema = z.object({
  parentRunId: z.string().uuid(),
  status: ParentRunStatusSchema,
  children: z.array(ParallelChildSchema),
});
export type StartParallelRunResponse = z.infer<typeof StartParallelRunResponseSchema>;

export const ParallelRunDetailResponseSchema = z.object({
  parentRunId: z.string().uuid(),
  flowId: z.string(),
  flowName: z.string(),
  status: ParentRunStatusSchema,
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  durationMs: z.number().int().nullable(),
  /** Aggregate counts. `total = passed + failed + cancelled + running`. */
  total: z.number().int().nonnegative(),
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  cancelled: z.number().int().nonnegative(),
  running: z.number().int().nonnegative(),
  children: z.array(ParallelChildSchema),
});
export type ParallelRunDetailResponse = z.infer<typeof ParallelRunDetailResponseSchema>;

/**
 * Aggregate child statuses into a parent status using the rules
 * from the design doc:
 *
 *   - any child still running OR queued → parent = 'running'
 *   - all children cancelled            → parent = 'cancelled'
 *   - all children passed               → parent = 'passed'
 *   - all children failed               → parent = 'failed'
 *   - any pass + any fail               → parent = 'partial'
 *   - mix of pass/fail/cancelled        → 'partial' if there's a pass,
 *                                         'failed' otherwise
 *
 * Pure function — used by the service when persisting and by tests.
 */
export function aggregateChildStatus(
  children: ReadonlyArray<{ status: z.infer<typeof RunStatusSchema> }>,
): ParentRunStatus {
  if (children.length === 0) return 'queued';

  let queued = 0;
  let running = 0;
  let passed = 0;
  let failed = 0;
  let cancelled = 0;

  for (const child of children) {
    switch (child.status) {
      case 'queued':
        queued++;
        break;
      case 'running':
        running++;
        break;
      case 'passed':
        passed++;
        break;
      case 'failed':
        failed++;
        break;
      case 'cancelled':
        cancelled++;
        break;
    }
  }

  if (running + queued > 0) return 'running';
  if (cancelled === children.length) return 'cancelled';
  if (passed === children.length) return 'passed';
  if (failed === children.length) return 'failed';
  if (passed > 0 && failed > 0) return 'partial';
  if (passed > 0) return 'partial';
  return 'failed';
}

/** Re-export so frontend consumers don't have to deep-import. */
export { capabilityKey };
